import { EquirectangularReflectionMapping } from 'three';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { clone as cloneGltfScene } from 'three/addons/utils/SkeletonUtils.js';

const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/versioned/decoders/1.5.6/';
const KTX2_TRANSCODER_PATH = 'https://unpkg.com/three@0.160.0/examples/jsm/libs/basis/';

let gltfLoader = null;
let rgbeLoader = null;
let dracoLoader = null;
let ktx2Loader = null;
const assetCache = new Map();
const weaponAssetPromises = new Map();
let manifestCache = null;
let tipCursor = 0;
const loadingTips = [
  'Dash through gaps before the horde surrounds you',
  'Rail shots pierce, shotgun bursts clear close packs',
  'Boss waves arrive every fifth wave',
  'Tab opens the weapon wheel and slows time',
  'Low health triggers heartbeat audio and HUD pulse',
];

export function initLoaders(options = {}) {
  const { renderer = null } = options;
  gltfLoader = new GLTFLoader();
  rgbeLoader = new RGBELoader();

  dracoLoader = dracoLoader || new DRACOLoader().setDecoderPath(DRACO_DECODER_PATH);
  gltfLoader.setDRACOLoader(dracoLoader);

  if (renderer) {
    ktx2Loader = ktx2Loader || new KTX2Loader().setTranscoderPath(KTX2_TRANSCODER_PATH);
    ktx2Loader.detectSupport(renderer);
    gltfLoader.setKTX2Loader(ktx2Loader);
  }

  return {
    gltfLoader,
    rgbeLoader,
    dracoLoader,
    ktx2Loader,
  };
}

export function getGltfLoader() {
  if (!gltfLoader) initLoaders();
  return gltfLoader;
}

export function getRgbeLoader() {
  if (!rgbeLoader) initLoaders();
  return rgbeLoader;
}

export function setCompressionLoaders({ draco = null, ktx2 = null } = {}) {
  dracoLoader = draco || dracoLoader;
  ktx2Loader = ktx2;

  if (gltfLoader) {
    if (dracoLoader) gltfLoader.setDRACOLoader(dracoLoader);
    if (ktx2Loader) gltfLoader.setKTX2Loader(ktx2Loader);
  }
}

export function loadGltf(url, onProgress) {
  return getGltfLoader().loadAsync(publicAssetUrl(url), onProgress);
}

export function loadHdr(url, onProgress) {
  return assertLoadable(publicAssetUrl(url)).then((safeUrl) => getRgbeLoader().loadAsync(safeUrl, onProgress)).then((texture) => {
    texture.mapping = EquirectangularReflectionMapping;
    return texture;
  });
}

export function cloneGltf(gltfOrScene) {
  return cloneGltfScene(gltfOrScene.scene || gltfOrScene);
}

export function disposeLoaders() {
  dracoLoader?.dispose?.();
  ktx2Loader?.dispose?.();
  gltfLoader = null;
  rgbeLoader = null;
  dracoLoader = null;
  ktx2Loader = null;
}

export async function loadAssets(onProgress = () => {}) {
  const urls = await collectManifestUrls({ includeWeapons: false });
  const hdrUrls = ['hdr/night_urban_1k.hdr'];
  const queue = [...urls, ...hdrUrls];
  const total = queue.length;
  let loaded = 0;

  updateLoadingOverlay(loaded, total, 'Loading protocol assets');
  onProgress(loaded, total);

  for (const url of queue) {
    try {
      const asset = url.endsWith('.hdr') ? await loadHdr(url) : await loadGltf(url);
      assetCache.set(url, asset);
    } catch (error) {
      console.warn(`[loaders] optional asset failed: ${url}`, error);
      assetCache.set(url, null);
    }
    loaded += 1;
    updateLoadingOverlay(loaded, total, url);
    onProgress(loaded, total);
  }

  finishLoadingOverlay();
  return {
    gltf: getGltfLoader(),
    rgbe: getRgbeLoader(),
    assets: assetCache,
  };
}

export function getLoadedAsset(url) {
  return assetCache.get(publicAssetUrl(url)) || null;
}

export function getEnemyAsset(type = 'grunt') {
  const enemies = manifestCache?.enemies || {};
  const group = enemies[type] || enemies.ranged || enemies.grunt;
  const entry = chooseManifestVariant(group);
  if (!entry?.url) return null;
  return {
    entry,
    asset: getLoadedAsset(entry.url),
  };
}

export function getHeroAsset(heroId = null) {
  const entry = (heroId && manifestCache?.heroes?.[heroId]) || manifestCache?.hero;
  if (!entry?.url) return null;
  return { entry, asset: getLoadedAsset(entry.url) };
}

export function getPropAsset(id) {
  const entry = manifestCache?.props?.[id] || manifestCache?.obstacles?.[id];
  if (!entry?.url) return null;
  return {
    entry,
    asset: getLoadedAsset(entry.url),
  };
}

export async function loadWeaponAsset(id) {
  const manifest = await loadManifest();
  const url = manifest?.weapons?.[id]?.url;
  if (!url) return null;
  if (assetCache.has(url)) return assetCache.get(url);
  if (weaponAssetPromises.has(url)) return weaponAssetPromises.get(url);

  const promise = loadGltf(url)
    .then((asset) => {
      assetCache.set(url, asset);
      return asset;
    })
    .catch((error) => {
      console.warn(`[loaders] optional weapon asset failed: ${url}`, error);
      assetCache.set(url, null);
      return null;
    });
  weaponAssetPromises.set(url, promise);
  return promise;
}

async function collectManifestUrls(options = {}) {
  const { includeWeapons = true } = options;
  const manifest = await loadManifest();
  if (!manifest) return [];

  const source = includeWeapons
    ? manifest
    : { ...manifest, weapons: {} };
  return [...new Set(readUrls(source).filter((url) => /\.(gltf|glb)$/i.test(url)))];
}

async function loadManifest() {
  if (manifestCache) return manifestCache;
  try {
    const response = await fetch('manifest.json', { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    manifestCache = await response.json();
    return manifestCache;
  } catch (error) {
    console.warn('[loaders] manifest unavailable; continuing with procedural assets.', error);
    return null;
  }
}

function readUrls(value, urls = []) {
  if (Array.isArray(value)) {
    value.forEach((item) => readUrls(item, urls));
  } else if (value && typeof value === 'object') {
    for (const [key, child] of Object.entries(value)) {
      if (key === 'url' && typeof child === 'string') urls.push(publicAssetUrl(child));
      else readUrls(child, urls);
    }
  }
  return urls;
}

function chooseManifestVariant(entry) {
  if (!entry?.variants?.length) return entry;
  const variant = entry.variants[Math.floor(Math.random() * entry.variants.length)] || entry.variants[0];
  return {
    ...entry,
    ...variant,
    variants: undefined,
  };
}

function publicAssetUrl(url) {
  return String(url).replace(/^\.?\/*assets\//, '');
}

async function assertLoadable(url) {
  const response = await fetch(url, { cache: 'no-store' });
  const contentType = response.headers.get('content-type') || '';
  if (!response.ok || contentType.includes('text/html')) throw new Error(`Missing asset: ${url}`);
  return url;
}

function updateLoadingOverlay(loaded, total, tip) {
  const pct = total ? Math.round((loaded / total) * 100) : 100;
  const bar = document.getElementById('loadBar');
  const label = document.getElementById('loadTip');
  if (bar) bar.style.width = `${pct}%`;
  if (label) {
    const rotator = loadingTips[tipCursor++ % loadingTips.length];
    label.textContent = total ? `${pct}% - ${rotator}` : 'Preparing loop';
    label.title = tip;
  }
}

function finishLoadingOverlay() {
  const loading = document.getElementById('loadingScreen');
  const boot = document.getElementById('boot');
  if (boot) boot.classList.add('hidden');
  if (!loading) return;
  loading.style.opacity = '0';
  loading.style.pointerEvents = 'none';
  loading.style.transition = 'opacity 220ms ease';
  window.setTimeout(() => {
    loading.style.display = 'none';
  }, 240);
}
